import {
  getReportByPage,
  type ReportRequestOptions,
} from '@services/api/reports/byPage.api'
import type {
  ReportBucketType,
  ReportKpiScalarResult,
  ReportPeriodSeriesResult,
  ReportQueryParams,
} from '@services/api/reports/finance.api'

export interface ReportProviderRecallResponseRow {
  providerId: string
  provider: string
  eligible: number
  completed: number
  ratePct: number
}

export interface ReportProviderRecallResponseResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: ReportProviderRecallResponseRow[]
}

export interface ReportHygieneOverduePatientRow {
  patientId: string
  patientName: string
  lastHygiene: string | null
  daysSinceLastVisit: number
  phone: string
}

export interface ReportHygieneOverduePatientsResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: ReportHygieneOverduePatientRow[]
}

export interface ReportReactivationTrendBucket {
  label: string
  count: number
  percentage: number
}

export interface ReportReactivationTrendResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: ReportReactivationTrendBucket[]
}

export interface RecallReactivationControlReportResult {
  recallCompliance: ReportKpiScalarResult
  reactivationRate: ReportKpiScalarResult
  patientsDueForRecall: ReportKpiScalarResult
  hygieneOverdue: ReportKpiScalarResult
  recallComplianceTrend: ReportPeriodSeriesResult
  reactivationTrend: ReportReactivationTrendResult
  providerRecallResponse: ReportProviderRecallResponseResult
  hygieneOverduePatients: ReportHygieneOverduePatientsResult
}

export interface ReportNoShowCancellationTrendBucket {
  label: string
  noShowRate: number
  cancellationRate: number
}

export interface ReportNoShowCancellationTrendResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: ReportNoShowCancellationTrendBucket[]
}

export interface ReportRepeatOffenderRow {
  patientId: string
  patientName: string
  noShows: number
  cancellations: number
  lastEventDate: string
}

export interface ReportRepeatOffenderRowsResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: ReportRepeatOffenderRow[]
}

export interface ReportNoShowPatientRow {
  appointmentId: string
  patientName: string
  apptDate: string
  provider: string
  procedure: string
  phone: string
}

export interface ReportNoShowPatientRowsResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: ReportNoShowPatientRow[]
}

export interface ReportCancellationWindowBucket {
  label: string
  count: number
  percentage: number
}

export interface ReportCancellationWindowDistributionResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: ReportCancellationWindowBucket[]
}

export interface ScheduleReliabilityReportResult {
  noShowRate: ReportKpiScalarResult
  cancellationRate: ReportKpiScalarResult
  noShowCount: ReportKpiScalarResult
  repeatOffenders: ReportKpiScalarResult
  noShowCancellationTrend: ReportNoShowCancellationTrendResult
  repeatOffenderRows: ReportRepeatOffenderRowsResult
  noShowPatientRows: ReportNoShowPatientRowsResult
  cancellationWindowDistribution: ReportCancellationWindowDistributionResult
}

export interface ReportConversionFunnelStage {
  id: string
  label: string
  count: number
  value: number
}

export interface ReportConversionFunnelResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  stages: ReportConversionFunnelStage[]
}

export interface ReportUnscheduledOpportunityBucket {
  id: string
  label: string
  count: number
  value: number
}

export interface ReportUnscheduledOpportunitiesResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: ReportUnscheduledOpportunityBucket[]
}

export interface ReportExamUnscheduledProviderRow {
  providerId: string
  provider: string
  examDiagnoses: number
  unscheduledCount: number
  unscheduledPct: number
}

export interface ReportExamUnscheduledByProviderResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  rows: ReportExamUnscheduledProviderRow[]
}

export interface TreatmentConversionFunnelReportResult {
  treatmentAcceptanceRate: ReportKpiScalarResult
  unscheduledTreatmentValue: ReportKpiScalarResult & {
    patientCount: number
  }
  examUnscheduledCases: ReportKpiScalarResult
  completedVsDiagnosed: ReportKpiScalarResult
  conversionFunnel: ReportConversionFunnelResult
  unscheduledOpportunities: ReportUnscheduledOpportunitiesResult
  acceptanceRateTrend: ReportPeriodSeriesResult
  examUnscheduledByProvider: ReportExamUnscheduledByProviderResult
}

export type CallSentiment = 'Positive' | 'Neutral' | 'Negative'

export interface ReportCallVolumeOutcomesBucket {
  label: string
  total: number
  answered: number
  missed: number
}

export interface ReportCallVolumeOutcomesResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: ReportCallVolumeOutcomesBucket[]
}

export interface ReportMessagingPerformanceBucket {
  label: string
  inbound: number
  replied: number
}

export interface ReportMessagingPerformanceResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  buckets: ReportMessagingPerformanceBucket[]
}

export interface ReportCommunicationEffectivenessBar {
  id: string
  label: string
  labelLine1: string
  labelLine2: string
  rate: number
}

export interface ReportCommunicationEffectivenessResult {
  periodStart: string
  periodEnd: string
  bucket: ReportBucketType
  bars: ReportCommunicationEffectivenessBar[]
}

export interface ReportSentimentKpiResult extends ReportKpiScalarResult {
  dominantSentiment: CallSentiment | null
}

export interface CommunicationCenterPerformanceReportResult {
  totalCalls: ReportKpiScalarResult
  answered: ReportKpiScalarResult
  missed: ReportKpiScalarResult
  declined: ReportKpiScalarResult
  followUps: ReportKpiScalarResult
  sentiment: ReportSentimentKpiResult
  avgHandlingTimeSeconds: ReportKpiScalarResult
  callVolumeOutcomes: ReportCallVolumeOutcomesResult
  messagingPerformance: ReportMessagingPerformanceResult
  communicationEffectiveness: ReportCommunicationEffectivenessResult
}

export const getRecallReactivationControlReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<RecallReactivationControlReportResult> =>
  getReportByPage<RecallReactivationControlReportResult>(
    '/engagement/recall-reactivation-control',
    params,
    options,
  )

export const getScheduleReliabilityReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<ScheduleReliabilityReportResult> =>
  getReportByPage<ScheduleReliabilityReportResult>(
    '/engagement/schedule-reliability',
    params,
    options,
  )

export const getTreatmentConversionFunnelReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<TreatmentConversionFunnelReportResult> =>
  getReportByPage<TreatmentConversionFunnelReportResult>(
    '/engagement/treatment-conversion-funnel',
    params,
    options,
  )

export const getCommunicationCenterPerformanceReport = (
  params: ReportQueryParams,
  options?: ReportRequestOptions,
): Promise<CommunicationCenterPerformanceReportResult> =>
  getReportByPage<CommunicationCenterPerformanceReportResult>(
    '/engagement/communication-center-performance',
    params,
    options,
  )

export type { ReportQueryParams, ReportBucketType }
